/**
 * Work Portfolio – filters, pagination, hover, GSAP animations
 * Production-ready. @global gsap (optional)
 */
(function () {
  'use strict';

  var INIT_FLAG = 'wpPortfolioInit';
  var TITLE_SPLIT_FLAG = 'wpTitleSplit';
  var PER_PAGE_MIN = 2;
  var PER_PAGE_MAX = 24;
  var PER_PAGE_DEFAULT = 6;

  function normalizeLabel(label) {
    return (label || '').trim().toLowerCase();
  }

  function getCardCategories(card) {
    if (!card || !card.getAttribute) return [];
    var raw = (card.getAttribute('data-categories') || '').trim();
    if (!raw) return [];
    var parts = raw.split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim().toLowerCase();
      if (p) out.push(p);
    }
    return out;
  }

  function cardMatchesFilter(card, label, excludeLabels) {
    var raw = (label || '').toString().toLowerCase();
    var tokens = [];
    raw.split(',').forEach(function (s) {
      var t = s.trim();
      if (t) tokens.push(t);
    });

    if (!tokens.length || tokens.indexOf('all') !== -1) return true;

    var categories = getCardCategories(card);
    for (var i = 0; i < tokens.length; i++) {
      if (categories.indexOf(tokens[i]) === -1) continue;
      if (excludeLabels && excludeLabels.length) {
        for (var j = 0; j < excludeLabels.length; j++) {
          if (categories.indexOf(excludeLabels[j]) !== -1) return false;
        }
      }
      return true;
    }
    return false;
  }

  function parsePlatformFilters(str) {
    if (!str || typeof str !== 'string') return [];
    var out = [];
    str.split(',').forEach(function (s) {
      var t = s.trim().toLowerCase();
      if (t) out.push(t);
    });
    return out;
  }

  function animateHero(section) {
    if (typeof gsap === 'undefined') return;
    var titleEl = section && section.querySelector('.work-portfolio__title');
    if (!titleEl) return;

    var pills = section.querySelectorAll('[data-filter-trigger]');

    // Pick the currently visible tagline (dual-instance pattern)
    var tagline = null;
    var taglineCandidates = section.querySelectorAll('.work-portfolio__tagline');
    for (var i = 0; i < taglineCandidates.length; i++) {
      if (getComputedStyle(taglineCandidates[i]).display !== 'none') {
        tagline = taglineCandidates[i];
        break;
      }
    }

    // Lock title height BEFORE hiding — prevents scramble reflow between line counts
    var naturalHeight = titleEl.offsetHeight;
    gsap.set(titleEl, { opacity: 0, minHeight: naturalHeight });
    if (tagline) gsap.set(tagline, { opacity: 0, y: 12, willChange: 'transform, opacity' });
    if (pills.length) gsap.set(pills, { opacity: 0, y: 10, willChange: 'transform, opacity' });

    var originalText = titleEl.dataset.wpOriginalText || titleEl.textContent || '';
    titleEl.dataset.wpOriginalText = originalText;

    var hasScramble = typeof ScrambleTextPlugin !== 'undefined';
    var hasSplitText = typeof SplitText !== 'undefined';
    var taglineSplit = null;

    if (tagline && hasSplitText) {
      try {
        gsap.registerPlugin(SplitText);
        taglineSplit = new SplitText(tagline, { type: 'chars', specialChars: '.,!?:;+-=&/%$#@<>' });
        if (taglineSplit.chars && taglineSplit.chars.length) {
          gsap.set(taglineSplit.chars, { opacity: 0, yPercent: 100, willChange: 'transform, opacity' });
        }
      } catch (e) {
        taglineSplit = null;
      }
    }

    var tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

    // Step 1 — Title: scramble or fade+slide
    if (hasScramble && originalText) {
      tl.to(titleEl, {
        duration: 1.2,
        opacity: 1,
        scrambleText: {
          text: originalText,
          chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
          revealDelay: 0.2,
          speed: 0.5,
        },
        ease: 'none',
        onComplete: function () { gsap.set(titleEl, { minHeight: '' }); },
      });
    } else {
      tl.fromTo(titleEl, { opacity: 0, y: 24 }, {
        opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
        onComplete: function () { gsap.set(titleEl, { minHeight: '' }); },
      });
    }

    // Step 2+3 — Tagline + pills simultaneously via shared label
    tl.addLabel('reveal', '+=0.3');

    if (taglineSplit && taglineSplit.chars && taglineSplit.chars.length) {
      tl.to(taglineSplit.chars, { opacity: 1, yPercent: 0, duration: 0.3, stagger: 0.01, ease: 'expo.out' }, 'reveal');
    } else if (tagline) {
      tl.to(tagline, { opacity: 1, y: 0, duration: 0.3 }, 'reveal');
    }
    if (pills.length) tl.to(pills, { opacity: 1, y: 0, duration: 0.2, stagger: 0.08, ease: 'power2.out' }, 'reveal');
  }

  function initSection(section) {
    if (!section || !section.getAttribute) return;
    if (section.dataset[INIT_FLAG] === '1') return;
    section.dataset[INIT_FLAG] = '1';

    var defaultId = section.getAttribute('data-default-filter');
    var filterManual = section.getAttribute('data-filter-manual') === 'manual';
    var perPageRaw = parseInt(section.getAttribute('data-cards-per-page') || String(PER_PAGE_DEFAULT), 10);
    var perPage = isNaN(perPageRaw) ? PER_PAGE_DEFAULT : Math.max(PER_PAGE_MIN, Math.min(PER_PAGE_MAX, perPageRaw));
    var paginationStyle = (section.getAttribute('data-pagination') || 'load_more').toLowerCase();
    var showLoadMore = paginationStyle === 'load_more';

    var platformFilters = parsePlatformFilters(section.getAttribute('data-platform-filters') || '');

    var triggers = [].slice.call(section.querySelectorAll('[data-filter-trigger]'));
    var cards = [].slice.call(section.querySelectorAll('[data-work-card]'));
    var loadMoreWrap = section.querySelector('[data-load-more-wrap]');
    var loadMoreBtn = section.querySelector('[data-load-more]');
    var mediaWraps = [].slice.call(section.querySelectorAll('[data-card-media]'));
    var useGsap = typeof gsap !== 'undefined';

    var filterBar = section.querySelector('[data-filter-bar]');
    var filterBarDetails = section.querySelector('[data-filter-bar-details]');
    var filterBarTrigger = section.querySelector('[data-filter-bar-trigger]');
    var filterBarValue = section.querySelector('[data-filter-bar-value]');
    var filterBarOptions = [].slice.call(section.querySelectorAll('[data-filter-bar-option]'));
    var filterBarPills = section.querySelector('.work-portfolio__filters');
    var filterBarFooter = typeof document !== 'undefined' ? document.querySelector('footer') : null;

    var loadedCount = 0;
    var currentVisibleCards = [];
    var heroObs = null;
    var barRaf = null;
    var openScrollY = 0;
    var closeTimer = null;
    var CLOSE_FADE_MS = 200; /* must match the panel's opacity transition */
    var revealTimer = null;
    var revealScrollY = 0;
    var REVEAL_HOLD_MS = 350; /* longer than a flick spends crossing a short grid */
    var REVEAL_LEAD = 3; /* holds of runway to keep in hand: arrive, sit a beat, leave */
    var boundHandlers = [];
    var mousemoveBindings = [];

    function getExcludeLabels(label) {
      if (!filterManual || !label || label === 'all' || platformFilters.indexOf(label) === -1) return [];
      var out = [];
      for (var i = 0; i < platformFilters.length; i++) {
        if (platformFilters[i] !== label) out.push(platformFilters[i]);
      }
      return out;
    }

    function setActiveFilter(button) {
      // Match by label, not identity: a hero pill and its twin in the filter bar share a label and
      // must both reflect the single active filter.
      var wanted = button ? normalizeLabel(button.getAttribute('data-filter-label')) : '';
      for (var i = 0; i < triggers.length; i++) {
        var trigger = triggers[i];
        var active = normalizeLabel(trigger.getAttribute('data-filter-label')) === wanted;
        if (trigger.getAttribute('role') === 'option') {
          trigger.setAttribute('aria-selected', active ? 'true' : 'false');
          trigger.setAttribute('tabindex', active ? '0' : '-1');
        } else {
          trigger.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
        trigger.classList.toggle('work-portfolio__filter-pill--active', active);
      }
      syncFilterBarValue(button);
    }

    // The bar names whichever filter is active, including pills it does not list itself. When the
    // active filter is one of the bar's own options, that option's label wins so the bar can read
    // "Everything" while the pill still reads "All".
    function syncFilterBarValue(button) {
      if (!filterBarValue || !button) return;
      var label = normalizeLabel(button.getAttribute('data-filter-label'));
      var source = button;
      for (var i = 0; i < filterBarOptions.length; i++) {
        if (normalizeLabel(filterBarOptions[i].getAttribute('data-filter-label')) === label) {
          source = filterBarOptions[i];
          break;
        }
      }
      var display = source.getAttribute('data-filter-display');
      if (!display) display = (source.textContent || '').trim();
      filterBarValue.textContent = display;
    }

    // Filtering from the floating bar shortens the grid under the user, which used to leave them
    // stranded in the footer (BugHerd #130). Every selection returns to the top of the page instead.
    // Instant, because html carries scroll-behavior: smooth and animating the jump from deep in the
    // page is exactly the redirect that was reported.
    function scrollToTop() {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    function queueFilterBarUpdate() {
      if (barRaf === null) barRaf = requestAnimationFrame(updateFilterBar);
    }

    function prefersReducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    function cancelFilterBarReveal() {
      if (revealTimer !== null) {
        clearTimeout(revealTimer);
        revealTimer = null;
      }
    }

    // Distance still to scroll before the footer forces the bar back out.
    function filterBarRunway() {
      if (!filterBarFooter || !filterBar) return Infinity;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return filterBarFooter.getBoundingClientRect().top - (vh - filterBar.offsetHeight);
    }

    // The hold has elapsed, so decide whether the entrance is still worth starting. Ground covered
    // during the hold is a direct read of scroll speed, so requiring several times that much runway
    // left means the bar only appears when it has room to arrive, sit and leave. The hold alone is
    // not enough on a mid-sized grid: SEO / AI is four cards, where a longer hold only reveals the
    // bar later and therefore closer to the footer that takes it away again. Re-measuring instead
    // of giving up means coming to a stop still brings the bar in, with no scroll event needed.
    function commitFilterBarReveal() {
      if (filterBarRunway() < Math.abs(window.scrollY - revealScrollY) * REVEAL_LEAD) {
        revealScrollY = window.scrollY;
        revealTimer = setTimeout(commitFilterBarReveal, REVEAL_HOLD_MS);
        return;
      }
      revealTimer = null;
      filterBar.setAttribute('data-visible', 'true');
    }

    function cancelFilterBarClose() {
      if (closeTimer !== null) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      if (filterBarDetails) filterBarDetails.removeAttribute('data-closing');
    }

    // Fade the panel out before actually closing the <details>. The UA stops rendering details
    // content the instant `open` is removed, which kills any transition mid-flight, so the element
    // has to stay open for the length of the fade and only then close. Without this the panel is
    // yanked away with no fade-out at all, which is the "hides and shows is not so smooth" report
    // in SSM-532. Escape and outside-click still close instantly: those are handled by
    // accordion-custom.js and auto-close-details.js, which flip `open` directly.
    function closeFilterBar(animate) {
      if (!filterBarDetails || !filterBarDetails.open) return;
      if (!animate || prefersReducedMotion()) {
        cancelFilterBarClose();
        filterBarDetails.open = false;
        return;
      }
      if (closeTimer !== null) return;
      filterBarDetails.setAttribute('data-closing', '');
      // The toggle event cannot report this yet, since `open` is deliberately still set.
      if (filterBarTrigger) filterBarTrigger.setAttribute('aria-expanded', 'false');
      closeTimer = setTimeout(function () {
        closeTimer = null;
        filterBarDetails.removeAttribute('data-closing');
        filterBarDetails.open = false;
      }, CLOSE_FADE_MS);
    }

    // The trigger is a <summary>, so closing is the browser's default. Cancel it and run the fade
    // instead; opening keeps the native behaviour.
    function onFilterBarTriggerClick(ev) {
      if (!filterBarDetails) return;
      if (closeTimer !== null) {
        // Tapped again mid-fade: stay open rather than flickering shut and straight back open.
        ev.preventDefault();
        cancelFilterBarClose();
        if (filterBarTrigger) filterBarTrigger.setAttribute('aria-expanded', 'true');
        return;
      }
      if (filterBarDetails.open) {
        ev.preventDefault();
        closeFilterBar(true);
      }
    }

    function updateFilterBar() {
      barRaf = null;
      if (!filterBar) return;

      var vh = window.innerHeight || document.documentElement.clientHeight;
      var barH = filterBar.offsetHeight || 0;
      var anchor = filterBarPills || section.querySelector('.work-portfolio__hero-wrap');
      var anchorBottom = anchor ? anchor.getBoundingClientRect().bottom : -1;

      // Reveal as soon as the hero pills leave the viewport: past that point the bar is the only
      // filter control left on screen.
      var pillsGone = anchorBottom <= 0;

      // Hide wherever the footer would sit behind the bar.
      var footerCovers = filterBarFooter
        ? filterBarFooter.getBoundingClientRect().top <= vh - barH
        : false;

      var show = pillsGone && !footerCovers;

      // Reveal on a hold, hide at once. On a short filtered view the stretch between the pills
      // leaving and the footer arriving is shorter than one flick, so the bar used to start its
      // 0.3s slide-in and reverse before finishing, which reads as a glitch rather than a control
      // (CRO & Optimization is three cards: 200ms on screen). Making the entrance wait means a
      // flick straight past never starts it, while anyone scrolling the grid at reading pace still
      // gets the bar. Gating on scroll speed rather than page shape is deliberate: measured against
      // live data the window ranges from -0.12vh to 6.51vh with no gap to put a threshold in, and
      // the reported glitch (CRO, 1.01vh on mobile) sits below a view that must keep its bar
      // (Design & Dev., 1.15vh on desktop). Hiding stays instant so the bar never rides the footer.
      if (!show) {
        cancelFilterBarReveal();
        filterBar.setAttribute('data-visible', 'false');
      } else if (filterBar.getAttribute('data-visible') !== 'true' && revealTimer === null) {
        revealScrollY = window.scrollY;
        revealTimer = setTimeout(commitFilterBarReveal, REVEAL_HOLD_MS);
      }

      // Close on scroll (SSM-532): the panel used to stay open all the way to the end of the page,
      // and a panel left open while the bar hides would slide back in already expanded. The 8px
      // slack keeps iOS rubber-banding and the browser's own scroll restoration from closing it
      // the instant it opens. Scrolling inside the option list does not fire window scroll, and
      // overscroll-behavior: contain stops it chaining out.
      if (filterBarDetails && filterBarDetails.open) {
        if (!show || Math.abs(window.scrollY - openScrollY) > 8) closeFilterBar(true);
      }
    }

    function applyFilterAndPagination(label) {
      var excludeLabels = getExcludeLabels(label);
      var visible = [];
      for (var i = 0; i < cards.length; i++) {
        if (cardMatchesFilter(cards[i], label, excludeLabels)) visible.push(cards[i]);
      }
      currentVisibleCards = visible;
      loadedCount = showLoadMore ? Math.min(perPage, visible.length) : visible.length;

      for (var j = 0; j < cards.length; j++) cards[j].classList.add('hidden');
      for (var k = 0; k < visible.length && k < loadedCount; k++) visible[k].classList.remove('hidden');

      if (loadMoreWrap && loadMoreBtn) {
        if (showLoadMore && visible.length > perPage && loadedCount < visible.length) {
          loadMoreWrap.removeAttribute('hidden');
        } else {
          loadMoreWrap.setAttribute('hidden', '');
        }
      }

      if (useGsap && visible.length > 0) {
        var toShow = visible.slice(0, loadedCount);
        gsap.fromTo(toShow, { opacity: 0, y: 16 }, { duration: 0.4, opacity: 1, y: 0, stagger: 0.05, delay: 0.05, ease: 'power2.out' });
      }

      // The grid length just changed, so the page-shape test that decides whether the footer may
      // hide the bar has to run again.
      queueFilterBarUpdate();
    }

    function onFilterClick() {
      var label = normalizeLabel(this.getAttribute('data-filter-label'));
      setActiveFilter(this);
      applyFilterAndPagination(label);
      // Only the bar moves the viewport. The pills sit at the top of the page already, so a pill
      // click must not scroll at all.
      if (this.hasAttribute('data-filter-bar-option')) {
        scrollToTop();
        closeFilterBar(true);
        if (filterBarTrigger) filterBarTrigger.focus({ preventScroll: true });
      }
    }

    // Roving arrow-key navigation inside the bar's listbox. Escape and outside-click come from
    // accordion-custom + auto-close-details.js.
    function onFilterBarKeydown(ev) {
      if (!filterBarOptions.length) return;
      var key = ev.key;
      if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') return;
      var current = filterBarOptions.indexOf(document.activeElement);
      var next;
      if (key === 'Home') {
        next = 0;
      } else if (key === 'End') {
        next = filterBarOptions.length - 1;
      } else if (current === -1) {
        next = key === 'ArrowDown' ? 0 : filterBarOptions.length - 1;
      } else {
        next = key === 'ArrowDown' ? current + 1 : current - 1;
        if (next < 0) next = filterBarOptions.length - 1;
        if (next >= filterBarOptions.length) next = 0;
      }
      ev.preventDefault();
      filterBarOptions[next].focus();
    }

    function onFilterBarToggle() {
      if (!filterBarDetails || !filterBarTrigger) return;
      var open = filterBarDetails.open;
      filterBarTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Baseline for close-on-scroll, so the measured delta starts from where the panel opened.
      if (open) openScrollY = window.scrollY;
      if (!open) return;
      var target = filterBarOptions[0];
      for (var i = 0; i < filterBarOptions.length; i++) {
        if (filterBarOptions[i].getAttribute('aria-selected') === 'true') {
          target = filterBarOptions[i];
          break;
        }
      }
      // Wait a frame so the panel has been painted; a still-hidden element cannot take focus.
      if (target) {
        requestAnimationFrame(function () {
          if (filterBarDetails.open) target.focus();
        });
      }
    }

    function getHashFilterLabel() {
      if (typeof window === 'undefined' || !window.location || !window.location.hash) return '';
      var h = window.location.hash.replace(/^#/, '').trim();
      if (!h) return '';
      try {
        h = decodeURIComponent(h);
      } catch (e) {}
      return h.toLowerCase();
    }

    function findTriggerByLabel(want) {
      var w = normalizeLabel(want);
      if (!w) return null;
      for (var i = 0; i < triggers.length; i++) {
        if (normalizeLabel(triggers[i].getAttribute('data-filter-label')) === w) {
          return triggers[i];
        }
      }
      return null;
    }

    function findDefaultTrigger() {
      if (defaultId) {
        for (var i = 0; i < triggers.length; i++) {
          if (triggers[i].getAttribute('data-filter-id') === String(defaultId)) return triggers[i];
        }
      }
      return triggers.length > 0 ? triggers[0] : null;
    }

    function onHashChange() {
      var hl = getHashFilterLabel();
      if (!hl) {
        var defBtn = findDefaultTrigger();
        if (defBtn) {
          setActiveFilter(defBtn);
          applyFilterAndPagination(normalizeLabel(defBtn.getAttribute('data-filter-label')));
        }
        return;
      }
      var btn = findTriggerByLabel(hl);
      if (!btn) return;
      var lab = normalizeLabel(btn.getAttribute('data-filter-label'));
      setActiveFilter(btn);
      applyFilterAndPagination(lab);
    }

    function onLoadMoreClick() {
      var nextEnd = Math.min(loadedCount + perPage, currentVisibleCards.length);
      for (var i = loadedCount; i < nextEnd; i++) {
        currentVisibleCards[i].classList.remove('hidden');
        if (useGsap) {
          gsap.fromTo(currentVisibleCards[i], { opacity: 0, y: 16 }, { duration: 0.35, opacity: 1, y: 0, delay: (i - loadedCount) * 0.05, ease: 'power2.out' });
        }
      }
      loadedCount = nextEnd;
      if (loadedCount >= currentVisibleCards.length && loadMoreWrap) loadMoreWrap.setAttribute('hidden', '');
    }

    for (var t = 0; t < triggers.length; t++) {
      // Don't clobber a role the markup already declares (the filter bar options are role="option").
      if (!triggers[t].getAttribute('role')) triggers[t].setAttribute('role', 'button');
      triggers[t].addEventListener('click', onFilterClick);
      boundHandlers.push({ el: triggers[t], ev: 'click', fn: onFilterClick });
    }

    if (filterBarDetails && filterBarOptions.length) {
      filterBarDetails.addEventListener('keydown', onFilterBarKeydown);
      filterBarDetails.addEventListener('toggle', onFilterBarToggle);
      boundHandlers.push(
        { el: filterBarDetails, ev: 'keydown', fn: onFilterBarKeydown },
        { el: filterBarDetails, ev: 'toggle', fn: onFilterBarToggle }
      );
      if (filterBarTrigger) {
        filterBarTrigger.setAttribute('aria-expanded', 'false');
        filterBarTrigger.addEventListener('click', onFilterBarTriggerClick);
        boundHandlers.push({ el: filterBarTrigger, ev: 'click', fn: onFilterBarTriggerClick });
      }
    }

    if (loadMoreBtn && loadMoreWrap) {
      loadMoreBtn.addEventListener('click', onLoadMoreClick);
      boundHandlers.push({ el: loadMoreBtn, ev: 'click', fn: onLoadMoreClick });
    }

    var hashLabel = getHashFilterLabel();
    var hashBtn = hashLabel ? findTriggerByLabel(hashLabel) : null;

    var label = 'all';
    var initialBtn = null;
    if (hashBtn) {
      initialBtn = hashBtn;
    } else if (defaultId && triggers.length > 0) {
      for (var k = 0; k < triggers.length; k++) {
        if (triggers[k].getAttribute('data-filter-id') === String(defaultId)) {
          initialBtn = triggers[k];
          break;
        }
      }
    }
    if (!initialBtn && triggers.length > 0) initialBtn = triggers[0];
    if (initialBtn) {
      label = normalizeLabel(initialBtn.getAttribute('data-filter-label'));
      setActiveFilter(initialBtn);
    }
    applyFilterAndPagination(label);

    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', onHashChange);
      boundHandlers.push({ el: window, ev: 'hashchange', fn: onHashChange });
    }

    if (typeof IntersectionObserver !== 'undefined') {
      var hero = section.querySelector('.work-portfolio__hero-wrap') || section;
      heroObs = new IntersectionObserver(
        function (entries) {
          for (var e = 0; e < entries.length; e++) {
            if (!entries[e].isIntersecting) continue;
            heroObs.unobserve(entries[e].target);
            animateHero(section);
            break;
          }
        },
        { threshold: 0.35 }
      );
      heroObs.observe(hero);
    } else {
      animateHero(section);
    }

    // Filter bar: reveal once the hero pills have scrolled out, hide again where the footer would
    // sit behind the bar so it never covers the newsletter signup. Measured on scroll rather than
    // with IntersectionObserver because filtering changes the document height under us, and because
    // two observers could only express "the footer is somewhere on screen", not "the footer is
    // behind the bar". On short result sets (B2B, Paid Media) the hero and footer were both
    // intersecting at every scroll position, so the bar never appeared at all (SSM-532).
    if (filterBar) {
      filterBar.removeAttribute('hidden');
      window.addEventListener('scroll', queueFilterBarUpdate, { passive: true });
      window.addEventListener('resize', queueFilterBarUpdate);
      boundHandlers.push(
        { el: window, ev: 'scroll', fn: queueFilterBarUpdate },
        { el: window, ev: 'resize', fn: queueFilterBarUpdate }
      );
      updateFilterBar();
    }

    for (var m = 0; m < mediaWraps.length; m++) {
      (function (wrap) {
        var card = wrap.closest('[data-work-card]');
        if (!card) return;
        var video = wrap.querySelector('[data-card-video]');
        var link = wrap.closest('.work-portfolio__card-link');
        var viewWork = wrap.querySelector('[data-card-view-work]');
        var followShouldRun = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: fine)').matches && !!viewWork;
        var rafId = null;
        var lastX = 0;
        var lastY = 0;

        function onMouseMove(ev) {
          lastX = ev.clientX;
          lastY = ev.clientY;
          if (rafId === null) {
            rafId = requestAnimationFrame(function () {
              rafId = null;
              if (!viewWork) return;
              var rect = wrap.getBoundingClientRect();
              if (!rect.width || !rect.height) return;
              var relX = (lastX - rect.left) / rect.width;
              var relY = (lastY - rect.top) / rect.height;
              var leftPct = Math.min(95, Math.max(5, relX * 100));
              var topPct = Math.min(95, Math.max(0, relY * 100));
              if (useGsap) {
                gsap.to(viewWork, { duration: 0.25, left: leftPct + '%', top: topPct + '%', ease: 'expo.out', overwrite: true });
              } else {
                viewWork.style.left = leftPct + '%';
                viewWork.style.top = topPct + '%';
              }
            });
          }
        }

        function onEnter() {
          wrap.classList.add('is-hovered');
          wrap.classList.add('is-video-active');
          if (video) {
            try {
              video.currentTime = 0;
              video.play().catch(function () {});
            } catch (err) {}
          }
          if (followShouldRun && viewWork) {
            try {
              wrap.addEventListener('mousemove', onMouseMove, { passive: true });
            } catch (e) {
              wrap.addEventListener('mousemove', onMouseMove);
            }
            mousemoveBindings.push({ el: wrap, fn: onMouseMove });
          }
        }

        function onLeave() {
          wrap.classList.remove('is-hovered');
          wrap.classList.remove('is-video-active');
          if (video) {
            try { video.pause(); } catch (err) {}
          }
          wrap.removeEventListener('mousemove', onMouseMove);
          if (rafId !== null) cancelAnimationFrame(rafId);
        }

        card.addEventListener('mouseenter', onEnter);
        card.addEventListener('mouseleave', onLeave);
        boundHandlers.push({ el: card, ev: 'mouseenter', fn: onEnter }, { el: card, ev: 'mouseleave', fn: onLeave });
        if (link) {
          link.addEventListener('focus', onEnter);
          link.addEventListener('blur', onLeave);
          boundHandlers.push({ el: link, ev: 'focus', fn: onEnter }, { el: link, ev: 'blur', fn: onLeave });
        }
      })(mediaWraps[m]);
    }

    section._wpCleanup = function () {
      if (heroObs) heroObs.disconnect();
      if (barRaf !== null) cancelAnimationFrame(barRaf);
      cancelFilterBarReveal();
      cancelFilterBarClose();
      for (var b = 0; b < boundHandlers.length; b++) {
        boundHandlers[b].el.removeEventListener(boundHandlers[b].ev, boundHandlers[b].fn);
      }
      for (var mm = 0; mm < mousemoveBindings.length; mm++) {
        mousemoveBindings[mm].el.removeEventListener('mousemove', mousemoveBindings[mm].fn);
      }
      delete section.dataset[INIT_FLAG];
      var titleEl = section.querySelector('.work-portfolio__title');
      if (titleEl && titleEl.dataset[TITLE_SPLIT_FLAG] === '1' && titleEl.dataset.wpOriginalText) {
        titleEl.textContent = titleEl.dataset.wpOriginalText;
        delete titleEl.dataset[TITLE_SPLIT_FLAG];
        delete titleEl.dataset.wpOriginalText;
      }
      delete section._wpCleanup;
    };
  }

  function cleanupSection(container) {
    if (!container) return;
    var sections = [];
    if (container.getAttribute && container.getAttribute('data-work-portfolio') !== null) sections.push(container);
    var nested = container.querySelectorAll ? container.querySelectorAll('[data-work-portfolio]') : [];
    for (var i = 0; i < nested.length; i++) {
      if (nested[i] !== container) sections.push(nested[i]);
    }
    for (var j = 0; j < sections.length; j++) {
      if (typeof sections[j]._wpCleanup === 'function') sections[j]._wpCleanup();
    }
  }

  function initAll(container) {
    if (!container) return;
    var sections = container.querySelectorAll ? container.querySelectorAll('[data-work-portfolio]') : [];
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    cleanupSection(e.target);
  });
})();
